import { API_ENDPOINT, fetchWithAuth, parseApiResponse } from ".";
import type { EventPricingConfig } from "../types/pricingConfig";
import type { AdminTicketType } from "./tickets";
import type { AdminTicketSet } from "./ticketSets";

export interface TicketType {
  id: number;
  name: string;
  description: string | null;
  pricePence: number;
  currency: string;
  quantityTotal: number | null;
  quantitySold: number;
  status: string;
}

export type EventRegistrationMode = "tickets" | "invitation" | "conference";

export interface EventSummary {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  description: string;
  venueName: string | null;
  venueAddress: string | null;
  city: string | null;
  country: string | null;
  startDate: string;
  endDate: string | null;
  registrationOpensAt: string | null;
  registrationClosesAt: string | null;
  coverImageUrl: string | null;
  websiteUrl: string | null;
  contactEmail: string | null;
  registrationMode: EventRegistrationMode;
  tags: string[] | null;
  status: string;
  testPricingEnabled?: boolean;
  onsiteRegistrationOpen?: boolean;
}

export interface ManageEvent extends EventSummary {
  testPricingEnabled: boolean;
  onsiteRegistrationOpen: boolean;
  pricingConfig?: EventPricingConfig | null;
  ticketTypes?: AdminTicketType[];
  ticketSets?: AdminTicketSet[];
}

export interface EventInput {
  title: string;
  slug?: string;
  excerpt?: string;
  description: string;
  venueName?: string;
  venueAddress?: string;
  city?: string;
  country?: string;
  startDate: string;
  endDate?: string | null;
  registrationOpensAt?: string;
  registrationClosesAt?: string;
  coverImageUrl?: string | null;
  websiteUrl?: string | null;
  contactEmail?: string | null;
  registrationMode?: EventRegistrationMode;
  tags?: string[] | null;
  status?: "draft" | "published" | "archived";
  pricingConfig?: EventPricingConfig | null;
}

export interface EventDetail extends EventSummary {
  ticketTypes: TicketType[];
}

export interface ListEventsOptions {
  status?: "draft" | "published" | "archived";
  search?: string;
  page?: number;
  limit?: number;
}

export async function listEvents(
  options: ListEventsOptions = {},
): Promise<EventSummary[]> {
  const params = new URLSearchParams();
  if (options.status) params.set("status", options.status);
  if (options.search) params.set("search", options.search);
  if (options.page) params.set("page", String(options.page));
  if (options.limit) params.set("limit", String(options.limit));

  const qs = params.toString();
  const url = `${API_ENDPOINT}/events${qs ? `?${qs}` : ""}`;
  const response = await fetch(url);
  return parseApiResponse<EventSummary[]>(response);
}

/** Authenticated list — super admins see draft/archived events too. */
export async function listEventsAuth(
  options: ListEventsOptions = {},
): Promise<EventSummary[]> {
  const params = new URLSearchParams();
  if (options.status) params.set("status", options.status);
  if (options.search) params.set("search", options.search);
  if (options.page) params.set("page", String(options.page));
  if (options.limit) params.set("limit", String(options.limit));

  const qs = params.toString();
  const url = `${API_ENDPOINT}/events${qs ? `?${qs}` : ""}`;
  const response = await fetchWithAuth(url);
  return parseApiResponse<EventSummary[]>(response);
}

export async function getEventBySlug(slug: string): Promise<EventDetail> {
  const response = await fetch(`${API_ENDPOINT}/events/${slug}`);
  const data = await parseApiResponse<{ event: EventDetail }>(response);
  return data.event;
}

export async function getEventForManage(id: number): Promise<ManageEvent> {
  const response = await fetchWithAuth(`${API_ENDPOINT}/events/manage/${id}`);
  const data = await parseApiResponse<{ event: ManageEvent }>(response);
  return data.event;
}

export async function createEvent(input: EventInput): Promise<ManageEvent> {
  const response = await fetchWithAuth(`${API_ENDPOINT}/events`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = await parseApiResponse<{ event: ManageEvent }>(response);
  return data.event;
}

export async function updateEvent(
  id: number,
  input: Partial<EventInput>,
): Promise<ManageEvent> {
  const response = await fetchWithAuth(`${API_ENDPOINT}/events/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  const data = await parseApiResponse<{ event: ManageEvent }>(response);
  return data.event;
}

export async function updateEventStatus(
  id: number,
  status: NonNullable<EventInput["status"]>,
): Promise<ManageEvent> {
  const response = await fetchWithAuth(`${API_ENDPOINT}/events/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
  const data = await parseApiResponse<{ event: ManageEvent }>(response);
  return data.event;
}

export async function setEventTestPricing(
  id: number,
  enabled: boolean,
): Promise<ManageEvent> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${id}/test-pricing`,
    {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    },
  );
  const data = await parseApiResponse<{ event: ManageEvent }>(response);
  return data.event;
}

export async function setEventOnsiteRegistration(
  id: number,
  enabled: boolean,
): Promise<ManageEvent> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${id}/onsite-registration`,
    {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    },
  );
  const data = await parseApiResponse<{ event: ManageEvent }>(response);
  return data.event;
}
